import React, { useMemo } from 'react';
import { useLang } from '../../../../util/lang';
import { FormControlLabel, Radio, RadioGroup, Tooltip } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import {
    NEW_PATIENT_RESTORATIVE_OPTIONS,
    NEW_PATIENT_RESTORATIVE_OPTIONS_SUB_VALUE,
    NEW_PATIENT_RESTORATIVE_OPTIONS_ONLAY,
    NEW_PATIENT_RESTORATIVE_OPTIONS_SUB_VALUE_VERTICAL_DIMENSION,
    NEW_PATIENT_RESTORATIVE_OPTIONS_POSTERIOR,
} from '../../../../store/NewPatient/actionType';
import styles from '../NewPatientPrescriptionNotes.module.css';
import { getWebAbsPath } from '../../../../util/path';
import { renderToothMarkers } from '../../NewPatientUtill';
import { RestorativeQuestion } from '../type';
import ErrorIcon from '@material-ui/icons/Error';
import VerticalDimensionPosteriorSelect from '../VerticalDimensionPosteriorSelect';

export const BASE_PRESCRIPTION_NOTES_LANG_KEY = 'prescriptionNotes';
const UPPER_POSTERIOR_TOOTH_NUMBERS = [1, 2, 3, 4, 11, 12, 13, 14]; // tooth numbers referenced from prescription notes tooth chart
const LOWER_POSTERIOR_TOOTH_NUMBERS = [17, 18, 19, 20, 27, 28, 29, 30]; // tooth numbers referenced from prescription notes tooth chart
const UPPER_TOOTH_NUMBERS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15]; // all upper-arch teeth on the chart
const LOWER_TOOTH_NUMBERS = [16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31]; // all lower-arch teeth on the chart

/**
 * RestorativeOptions component for displaying restorative treatment options
 * @param {Object} props - Component props
 * @param {RestorativeQuestion} props.question - The question object containing restorative options
 * @param {boolean} props.readOnly - Whether the component is in read-only mode
 * @param {Object} props.teeth - Teeth img object
 * @param {Function} props.handleToothClick - Handler for tooth click events
 * @param {Function} props.validation - Validation function
 */
const RestorativeOptions = ({ question, readOnly = false, teeth, handleToothClick, validation }) => {
    // console.log("q => ", question)
    const getNotes = useLang(`NewPatient`);
    const getLang = useLang();
    const newPatientReducer = useSelector((store) => store.newPatientReducer);
    const { restorativeOptions } = newPatientReducer;
    const { subValue } = restorativeOptions || {};
    const { onlayPosition, verticalDimension } = subValue || {};
    const dispatch = useDispatch();

    // const getIsRenderSubOptions = (hasSubOpts = false) => {
    //     if (!hasSubOpts) return false;
    //     return restorativeOptions.value === 'Yes';
    // };
    const getCustomLang = (key) => {
        return getLang('@NewPatient.prescriptionNotes.' + key);
    };

    const isRenderSubOptions = useMemo(() => {
        const { value, subValue } = restorativeOptions;
        return value === 'Yes' && subValue?.value?.length > 0;
    }, [restorativeOptions.value, restorativeOptions.subValue.value]);

    const handleTeethValueOnVerticalDimensionChange = (fieldName = '', value) => {
        if (!fieldName || !fieldName.length) return [];
        const targetValues =
            fieldName === 'verticalDimensionUpperPosterior'
                ? [...UPPER_POSTERIOR_TOOTH_NUMBERS]
                : [...LOWER_POSTERIOR_TOOTH_NUMBERS];

        if (value === '') {
            const archTeeth =
                fieldName === 'verticalDimensionUpperPosterior' ? UPPER_TOOTH_NUMBERS : LOWER_TOOTH_NUMBERS;
            return Array.isArray(onlayPosition) ? onlayPosition.filter((pos) => !archTeeth.includes(pos)) : [];
        }

        if (Array.isArray(onlayPosition)) {
            return Array.from(new Set([...targetValues, ...onlayPosition]));
        }

        return targetValues;
    };

    const handleVerticalDimensionChange = (e) => {
        if (readOnly) return;
        const { name, value } = e.target;
        let onlayPositionValues = handleTeethValueOnVerticalDimensionChange(name, value);
        if (validation && typeof validation === 'function') {
            if (!validation(name)) return;
        }
        dispatch({
            type: NEW_PATIENT_RESTORATIVE_OPTIONS_POSTERIOR,
            value: onlayPositionValues,
        });
        dispatch({
            type: NEW_PATIENT_RESTORATIVE_OPTIONS_SUB_VALUE_VERTICAL_DIMENSION,
            value: value,
            field: name,
        });
    };

    const isRenderToothReminderIcon = () => {
        const isOnlayValid = onlayPosition && Array.isArray(onlayPosition) && onlayPosition.length > 0;
        return restorativeOptions?.value === 'Yes' && !isOnlayValid;
    };

    const isRenderVerticalDimensionReminderIcon = useMemo(() => {
        if (restorativeOptions?.value === 'No') return false;
        const { verticalDimensionUpperPosterior, verticalDimensionLowerPosterior } = verticalDimension || {};
        const isUpperPosteriorInvalid = !verticalDimensionUpperPosterior || !verticalDimensionUpperPosterior?.length;
        const isLowerPosteriorInvalid = !verticalDimensionLowerPosterior || !verticalDimensionLowerPosterior?.length;
        return isUpperPosteriorInvalid && isLowerPosteriorInvalid;
    }, [
        restorativeOptions?.value,
        verticalDimension?.verticalDimensionUpperPosterior,
        verticalDimension?.verticalDimensionLowerPosterior,
    ]);

    const getPosteriorOptions = (options = [], oppositeValue) => {
        if (oppositeValue && oppositeValue === '0mm') {
            return options.map((opt) => (opt.value === '0mm' ? { ...opt, disabled: true } : opt));
        }
        return options;
    };

    const verticalDimensionUpperPosteriorOptions = useMemo(() => {
        return getPosteriorOptions(
            question.verticalDimension.verticalDimensionUpperPosterior.options,
            verticalDimension?.verticalDimensionLowerPosterior
        );
    }, [
        verticalDimension?.verticalDimensionLowerPosterior,
        question.verticalDimension.verticalDimensionUpperPosterior.options,
    ]);

    const verticalDimensionLowerPosteriorOptions = useMemo(() => {
        return getPosteriorOptions(
            question.verticalDimension.verticalDimensionLowerPosterior.options,
            verticalDimension?.verticalDimensionUpperPosterior
        );
    }, [
        verticalDimension?.verticalDimensionUpperPosterior,
        question.verticalDimension.verticalDimensionLowerPosterior.options,
    ]);

    return (
        <>
            <span id="restorativeOptions">{getNotes(`${BASE_PRESCRIPTION_NOTES_LANG_KEY}.restorativeOptions`)}</span>
            <br />
            <span style={{ paddingLeft: 30 }}>{getNotes(`${BASE_PRESCRIPTION_NOTES_LANG_KEY}.additionalFee`)}</span>

            <RadioGroup value={restorativeOptions.value}>
                {question.options.map((opt, idx) => {
                    // const hasSubOpts = opt.options && Array.isArray(opt.options) && opt.options.length > 0;
                    return (
                        <>
                            <FormControlLabel
                                key={idx.toString() + opt.value}
                                value={opt.value}
                                label={opt.label}
                                control={<Radio />}
                                onChange={(e) => {
                                    if (readOnly) return;
                                    dispatch({
                                        type: NEW_PATIENT_RESTORATIVE_OPTIONS,
                                        value: e.target.value,
                                    });
                                }}
                            />
                            {/* {getIsRenderSubOptions(hasSubOpts) && (
                                <RadioGroup
                                    id={`Q.restorativeOptionsSubValue`}
                                    className={styles.subOptions}
                                    value={restorativeOptions.subValue.value}
                                >
                                    {opt.options?.map((opt, idx) => {
                                        return (
                                            <FormControlLabel
                                                key={idx.toString() + opt.value}
                                                value={opt.value}
                                                label={opt.label}
                                                control={<Radio />}
                                                onChange={(e) => {
                                                    if (readOnly) return;
                                                    if (validation && typeof validation === 'function') {
                                                        if (!validation('restorativeOptionsSubValue')) return;
                                                    }
                                                    dispatch({
                                                        type: NEW_PATIENT_RESTORATIVE_OPTIONS_SUB_VALUE,
                                                        value: e.target.value,
                                                        field: 'value',
                                                    });
                                                }}
                                            />
                                        );
                                    })}
                                </RadioGroup>
                            )} */}
                        </>
                    );
                })}
                {isRenderSubOptions && (
                    <>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <span style={{ fontWeight: 'bold', padding: 10 }}>
                                {getNotes(`${BASE_PRESCRIPTION_NOTES_LANG_KEY}.verticalDimensionQuestion`)}
                                {isRenderVerticalDimensionReminderIcon && (
                                    <Tooltip title={getLang(`@NewPatient.warning.verticalDimensionPosterior`)}>
                                        <span style={{ color: 'red' ,marginLeft: '5px' ,fontWeight:'400'}}>
                                            *
                                        </span>
                                    </Tooltip>
                                )}
                            </span>
                        </div>
                        <VerticalDimensionPosteriorSelect
                            value={subValue?.verticalDimension?.verticalDimensionUpperPosterior}
                            options={verticalDimensionUpperPosteriorOptions}
                            onChange={handleVerticalDimensionChange}
                            name="verticalDimensionUpperPosterior"
                            upperAndLowerInput={getNotes(`${BASE_PRESCRIPTION_NOTES_LANG_KEY}.upperAndLowerInput`)}
                            label={getNotes(`${BASE_PRESCRIPTION_NOTES_LANG_KEY}.upperPosterior`)}
                            errorPointShow={isRenderVerticalDimensionReminderIcon}
                        />
                        <VerticalDimensionPosteriorSelect
                            value={subValue?.verticalDimension?.verticalDimensionLowerPosterior}
                            options={verticalDimensionLowerPosteriorOptions}
                            onChange={handleVerticalDimensionChange}
                            name="verticalDimensionLowerPosterior"
                            upperAndLowerInput={getNotes(`${BASE_PRESCRIPTION_NOTES_LANG_KEY}.upperAndLowerInput`)}
                            label={getNotes(`${BASE_PRESCRIPTION_NOTES_LANG_KEY}.lowerPosterior`)}
                            errorPointShow={isRenderVerticalDimensionReminderIcon}
                        />
                        <span style={{ fontWeight: 'bold', padding: 10, marginTop: 20 }}>
                            {getNotes(`${BASE_PRESCRIPTION_NOTES_LANG_KEY}.onlayQuestion`)}
                        </span>
                        <div
                            className={styles.toothImages}
                            style={{ display: 'flex' }}
                            id={`Q.restorativeOptionsSubValueOnlayPosition`}
                        >
                            <div
                                style={{
                                    width: 1175,
                                    height: 300,
                                    'margin-top': '10px',
                                    position: 'relative',
                                    backgroundRepeat: 'no-repeat',
                                    backgroundImage: `url("${getWebAbsPath('/img/prescriptionNotes/teeth_number_CS6.ai.png')}")`,
                                }}
                                onClick={(e) => {
                                    if (readOnly) return;
                                    console.log('e', e);
                                    if (handleToothClick && typeof handleToothClick === 'function') {
                                        const dispatchFunc = (v) =>
                                            dispatch({ type: NEW_PATIENT_RESTORATIVE_OPTIONS_ONLAY, value: v });
                                        handleToothClick(e, dispatchFunc);
                                    }
                                }}
                            >
                                {Array.isArray(onlayPosition) &&
                                    renderToothMarkers(onlayPosition, teeth, '/img/prescriptionNotes/checkSuccess.png')}
                            </div>
                            {isRenderToothReminderIcon() && (
                                <div>
                                    <Tooltip title={getNotes(`${BASE_PRESCRIPTION_NOTES_LANG_KEY}.attachmentTooltip`)}>
                                        <ErrorIcon style={{ color: 'red' }} />
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </RadioGroup>
        </>
    );
};

export default RestorativeOptions;
